/**
 * Date Range Selector Types
 *
 * Type definitions for the unified date range selector component.
 */

/** Quick presets shown in the primary bar */
export type QuickPreset =
  | "today"
  | "yesterday"
  | "this_month";

/** Extended presets shown in the custom popover */
export type ExtendedPreset =
  | "this_week"
  | "last_week"
  | "last_month"
  | "last_7_days"
  | "last_30_days"
  | "last_90_days"
  | "ytd"
  | "all";

/** All preset types combined */
export type DatePreset = QuickPreset | ExtendedPreset;

/** Custom date range with explicit from/to dates */
export type CustomDateRange = {
  type: "custom";
  from: Date;
  to: Date;
};

/** The value type for the date range selector */
export type DateRangeValue = DatePreset | CustomDateRange;

/** Helper type to check if value is a custom range */
export function isCustomRange(value: DateRangeValue): value is CustomDateRange {
  return typeof value === "object" && value.type === "custom";
}

/** Props for the DateRangeSelector component */
export type DateRangeSelectorProps = {
  /** Current selected value */
  value: DateRangeValue;
  /** Callback when value changes */
  onChange: (value: DateRangeValue) => void;
  /** Optional className for styling */
  className?: string;
  /** Whether to show compact version (fewer quick presets) */
  compact?: boolean;
};

/** Configuration for preset display (generic) */
export type PresetConfig<T extends DatePreset = DatePreset> = {
  value: T;
  label: string;
  shortLabel?: string;
};

/** Quick preset config */
export type QuickPresetConfig = PresetConfig<QuickPreset>;

/** Extended preset config */
export type ExtendedPresetConfig = PresetConfig<ExtendedPreset>;

/** Quick presets configuration */
export const QUICK_PRESETS: QuickPresetConfig[] = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_month", label: "This Month", shortLabel: "Month" },
];

/** Extended presets configuration */
export const EXTENDED_PRESETS: ExtendedPresetConfig[] = [
  { value: "this_week", label: "This Week" },
  { value: "last_week", label: "Last Week" },
  { value: "last_month", label: "Last Month" },
  { value: "last_7_days", label: "Last 7 Days" },
  { value: "last_30_days", label: "Last 30 Days" },
  { value: "last_90_days", label: "Last 90 Days" },
  { value: "ytd", label: "Year to Date" },
  { value: "all", label: "All Time" },
];

